// node_modules
import { faChevronDown, faChevronUp, faCircleNodes, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useCallback, useContext, useEffect, useState } from "react";
// import ContentLoader from "react-content-loader";
// Contexts
import { SavedQueryContext, TopicModellingSearchContext, UserContext, WebsocketContext } from "Providers";
import { CaseContext } from "Stores";
// Components
import { AsynchronousWrapper, BorderedIconedTextblock, FindestButton, MagDocument, Pagination, RotatingCircleLoadingIndicator, UsPatentDocument } from "Components";
import { FindDocumentsQueryFields } from "../FindDocumentsQueryFields/FindDocumentsQueryFields";
import { QueryTabs } from "../QueryTabs";
import { SavedQueries } from "../SavedQueries/SavedQueries";
import { SavedQueryFields } from "../SavedQueryFields/SavedQueryFields";
// Types
import { TConcept, TDocumentSearchStrategyResults, TFindDocumentsDTO, TMagDocumentDTO, TModelledTopicDTO, TPatentSearchResult, TScienceSearchResult, TTechnology, TUseFetch, TUsPatentDTO } from "Types";
// Custom hooks
import { useFetch, usePagination } from "Hooks";
// Enums
import { CaseActionTypeEnum, SearchQueryTypeEnum, ToastTypeEnum } from "Enums";
// styles
import variables from "styles/colors.module.scss";
import styles from "./findDocuments.module.scss";
// Constants
import { LoggingConstants, RegexConstants, TechnologyConstants } from "Constants";
// Logics
import { BatchFunctionLogicSingleton, BookmarkLogicSingleton } from "Logics";
// Interfaces
import { ICaseSelectedTechnology, ICaseSetQueryCount } from "Interfaces";
// Helpers
import { PendoHelperSingleton, ToastHelperSingleton } from "Helpers";
// Controllers
import { ArticleControllerSingleton } from "Controllers";

// const ViewQueryLoader = () => (
//     <ContentLoader
//         animate={true}
//         height="15"
//         width="100%"
//         speed={2}
//         backgroundColor="#fcfcfc"
//         foregroundColor="#ecebeb"
//     >
//         <rect x="32" y="0" rx="5" ry="5" width="70%" height="15" /> 
//     </ContentLoader>
// );
// 
// const SearchButtonLoader = () => (
//     <ContentLoader
//         animate={true}
//         height="24"
//         width="90"
//         speed={2}
//         backgroundColor="#fcfcfc"
//         foregroundColor="#ecebeb"
//     >
//         <rect x="0" y="0" rx="5" ry="5" width="90" height="24" /> 
//     </ContentLoader>
// );

export const FindDocuments: FC = () => {
    // Const
    const amountOfResultsToSearchFor = 100;

    // Search form state
    const [technologies, setTechnologies] = useState<TTechnology[]>([]);
    const [selectedOriginalTechnology, setSelectedOriginalTechnology] = useState<TTechnology | undefined>(undefined);
    const [selectedSearchQueryType, setSelectedSearchQueryType] = useState<SearchQueryTypeEnum>(SearchQueryTypeEnum.ScienceArticle);
    const [isQueryCollapsed, setIsQueryCollapsed] = useState<boolean>(true);
    const [isTopicModellingSearch, setIsTopicModellingSearch] = useState<boolean>(false);

    // Search results state
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [patents, setPatents] = useState<TUsPatentDTO[]>([]);
    const [shownPatents, setShownPatents] = useState<TUsPatentDTO[]>([]);
    const [scienceArticles, setScienceArticles] = useState<TMagDocumentDTO[]>([]);
    const [amountOfResults, setAmountOfResults] = useState<number | undefined>(undefined);
    const [searchId, setSearchId] = useState<string | undefined>(undefined);

    // Contexts
    const { webSocketController } = useContext(WebsocketContext);
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { isCurrentUserFindest } = useContext(UserContext);
    const {
        modelledTopics, setModelledTopics, setSelectedModelledTopic,
        selectedModelledTopic, isTopicModellingSearchStarted, setIsTopicModellingSearchStarted
    } = useContext(TopicModellingSearchContext);
    const { savedQuery, updateSavedQuery, isSavedQueriesLoading } = useContext(SavedQueryContext);

    // Query count state
    const [caseQueryCount, setCaseQueryCount] = useState<number>(caseState.selectedCase?.queryCount ? caseState.selectedCase?.queryCount : 0);

    // Logic
    const isAllowedToPaginate = (): boolean => {
        if (isSearching) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Please wait until the current search is finished.");
            return false;
        }

        return true;
    };

    const onPaginate = (newPageNumber: number) => {
        if (isTopicModellingSearch) {
            const startIndex: number = (newPageNumber-1) * amountOfResultsToSearchFor;
            const endIndex: number = newPageNumber * amountOfResultsToSearchFor;
            getAndSetDecoupledDocumentsFromSelectedModelledTopicAsync(startIndex, endIndex);
        } else {
            handleSearchOnClick(newPageNumber);
        }
    };

    // Custom hooks
    const { isLoading: isConceptListLoading, didError: didConceptListError, errorMessage: conceptListErrorMessage, fetchedData: fetchedConceptList }: TUseFetch<TConcept[]> = useFetch(`api/cases/${caseState.selectedCaseId}/concepts?doIncludeAdditional=true`);
    const { currentPage, amountOfPages, paginateToNextPage, paginateToPreviousPage } = usePagination(amountOfResultsToSearchFor, amountOfResults, onPaginate, isAllowedToPaginate);

    // Logic
    const getAndSetDecoupledDocumentsFromSelectedModelledTopicAsync = useCallback(async (startIndex: number, endIndex: number): Promise<void> => {
        // safety-checks
        if (!selectedModelledTopic || !caseState.selectedCaseId) { return; }
        switch (selectedSearchQueryType) {
            case SearchQueryTypeEnum.ScienceArticle:
                getAndSetDecoupledScienceArticlesAsync(selectedModelledTopic.documentIds.slice(startIndex, endIndex), caseState.selectedCaseId);
                break;
            case SearchQueryTypeEnum.Patent:
                getAndSetDecoupledPatentsAsync(selectedModelledTopic.documentIds.slice(startIndex, endIndex), caseState.selectedCaseId);
                break;
            default:
                break;
        }
    }, [caseState.selectedCaseId, selectedModelledTopic, selectedSearchQueryType]);

    useEffect(() => {
        // safety-checks
        if (!selectedModelledTopic || !caseState.selectedCaseId) { return; }
        // on selectedModelledTopic change
        setAmountOfResults(selectedModelledTopic.documentIds.length);
        getAndSetDecoupledDocumentsFromSelectedModelledTopicAsync(0, amountOfResultsToSearchFor); 
    }, [getAndSetDecoupledDocumentsFromSelectedModelledTopicAsync, selectedModelledTopic, selectedSearchQueryType, caseState.selectedCaseId]);

    const getAndSetDecoupledScienceArticlesAsync = async (scienceArticleIds: (number | string)[], caseId: number): Promise<void> => {
        const decoupledScienceArticles: TMagDocumentDTO[] = await ArticleControllerSingleton
            .getMagArticlesAsync(scienceArticleIds, caseId);
        decoupledScienceArticles
            .forEach((scienceArticle: TMagDocumentDTO) =>
                scienceArticle.isBookmarked = BookmarkLogicSingleton.isResultBookmarked(scienceArticle.coworkerBookmarkOn, scienceArticle.scoutBookmarkOn));
        setPatents([]);
        setScienceArticles(decoupledScienceArticles);
    };

    const getAndSetDecoupledPatentsAsync = async (patentIds: (number | string)[], caseId: number): Promise<void> => {
        const decoupledPatents: TUsPatentDTO[] = await ArticleControllerSingleton
            .getUsPatentsAsync(patentIds, caseId);
        decoupledPatents
            .forEach((scienceArticle: TUsPatentDTO) =>
                scienceArticle.isBookmarked = BookmarkLogicSingleton.isResultBookmarked(scienceArticle.coworkerBookmarkOn, scienceArticle.scoutBookmarkOn));
        setScienceArticles([]);
        setPatents(decoupledPatents);
    };

    const buildMagDocumentDTOFromScienceSearchResult = (scienceSearchResult: TScienceSearchResult): TMagDocumentDTO => {
        return {
            id: scienceSearchResult.paperId,
            docType: scienceSearchResult.docType,
            matchedHits: scienceSearchResult.matchedHits,
            title: scienceSearchResult.title,
            isBookmarked: BookmarkLogicSingleton.isResultBookmarked(scienceSearchResult.coworkerBookmarkOn, scienceSearchResult.scoutBookmarkOn),
            year: scienceSearchResult.publishedYear,
            coworkerBookmarkOn: scienceSearchResult.coworkerBookmarkOn,
            scoutBookmarkOn: scienceSearchResult.scoutBookmarkOn,
            predictedTechnologies: scienceSearchResult.predictedTechnologies,
            foundUnits: "hitUnits" in scienceSearchResult.extras ? scienceSearchResult.extras["hitUnits"] : scienceSearchResult.foundUnits,
            openAccessUrl: "openAccess" in scienceSearchResult.extras ? scienceSearchResult.extras["openAccess"] : undefined
        };
    };

    const fromPatentSearchResultToUsPatentDTO = (patentSearchResult: TPatentSearchResult): TUsPatentDTO => {
        return {
            id: patentSearchResult.id,
            docType: patentSearchResult.docType,
            matchedHits: patentSearchResult.matchedHits,
            title: patentSearchResult.title,
            isBookmarked: BookmarkLogicSingleton.isResultBookmarked(patentSearchResult.coworkerBookmarkOn, patentSearchResult.scoutBookmarkOn),
            year: patentSearchResult.publishedYear,
            coworkerBookmarkOn: patentSearchResult.coworkerBookmarkOn,
            scoutBookmarkOn: patentSearchResult.scoutBookmarkOn,
            foundUnits: "hitUnits" in patentSearchResult.extras ? patentSearchResult.extras["hitUnits"] : undefined,
            country: patentSearchResult.country
        };
    };

    useEffect(() => {
        if (!fetchedConceptList || fetchedConceptList.length <= 0) {
            setTechnologies([]);
        } else {
            const newTechnologyList: TTechnology[] = [];
            for (const concept of fetchedConceptList) {
                newTechnologyList.push(...concept.technologies);
            }
            setTechnologies(newTechnologyList);
        }
    }, [fetchedConceptList]);

    useEffect(() => {
        for (const technology of technologies) {
            if ((!technology.isUserAdded && technology.id === caseState.selectedOriginalTechnologyId) || 
                    (technology.isUserAdded && technology.id === caseState.selectedTechnologyId)) {
                setSelectedOriginalTechnology(technology);
                break;
            }
        }
    }, [caseState.selectedOriginalTechnologyId, caseState.selectedTechnologyId, technologies]);

    const filterPatents = useCallback((patentsToFilter: TUsPatentDTO[]): TUsPatentDTO[] => {
        if(!savedQuery || !savedQuery.filters || !savedQuery.filters.patentFilters) return patentsToFilter;
        // Filter type is None
        if(!savedQuery.filters.patentFilters.countryFilterType || savedQuery.filters.patentFilters.countryFilterType === 0) return patentsToFilter;
        if(!savedQuery.filters.patentFilters.filteredCountries) return patentsToFilter;

        const filteredPatents: TUsPatentDTO[] = [];

        for (const patent of patentsToFilter) {
            let doAdd = true;
            if(savedQuery.filters.patentFilters.countryFilterType === 1) {
                doAdd = false;
                for(const countryToFilterOn of savedQuery.filters.patentFilters.filteredCountries) {
                    // Filter type is show only
                    if(!patent.country || patent.country?.indexOf(countryToFilterOn) >= 0) {
                        doAdd = true;
                    }
                }                
            } else if (savedQuery.filters.patentFilters.countryFilterType === 2) {
                doAdd = true;
                for(const countryToFilterOn of savedQuery.filters.patentFilters.filteredCountries) {
                    // Filter type is exclude
                    if(!patent.country || patent.country?.toLocaleLowerCase().indexOf(countryToFilterOn.toLocaleLowerCase()) > -1) {
                        doAdd = false;
                    }
                }
            }

            if(doAdd) filteredPatents.push(patent);
        }
        return filteredPatents;
    }, [savedQuery]);

    useEffect(() => {
        if(!savedQuery || !savedQuery.filters) return;

        if(savedQuery.filters.patentFilters) {
            setShownPatents(filterPatents(patents));
        } else {
            setShownPatents(patents);
        }

        // TODO:prevent the dependency on saved query
    }, [filterPatents, patents, savedQuery, savedQuery?.filters?.patentFilters?.filteredCountries, savedQuery?.filters?.patentFilters?.countryFilterType]);

    const handleSearchOnClick = useCallback(async (paginateToPage: number, isClickOnTopicModellingSearch = false): Promise<void> => {
        if (!caseState.selectedCaseId || !savedQuery || !selectedOriginalTechnology) return;

        // Reset all the current state which is left from the last search
        BatchFunctionLogicSingleton.clearQueue("TechnologyPrediction");
        BatchFunctionLogicSingleton.clearQueue("UnitPrediction");
        setScienceArticles([]);
        setPatents([]);
        setSelectedModelledTopic(undefined);
        setModelledTopics([]);
        setShownPatents([]);
        if (!paginateToPage) {
            setAmountOfResults(undefined);
        }
        setIsTopicModellingSearch(isClickOnTopicModellingSearch);

        const filterVariables: TFindDocumentsDTO = {
            caseId: caseState.selectedCaseId,
            searchQueryType: isClickOnTopicModellingSearch ? SearchQueryTypeEnum.TopicModeling : selectedSearchQueryType,
            technologyId: selectedOriginalTechnology.id !== TechnologyConstants.GENERAL_TECHNOLOGY_ID ? selectedOriginalTechnology.id : undefined,
            amountOfResults: amountOfResultsToSearchFor,
            pageNumber: paginateToPage,
            savedQueryId: savedQuery.guid,
            topicModelingSearchQueryType: isClickOnTopicModellingSearch ? selectedSearchQueryType : undefined
        };

        switch (selectedSearchQueryType) {
            case SearchQueryTypeEnum.TopicModeling:
                PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TopicModellingSearch`, {
                    topicModelingSearchQueryType: isClickOnTopicModellingSearch ? selectedSearchQueryType : undefined,
                    caseId: caseState.selectedCaseId,
                    technologyId: selectedOriginalTechnology.id !== TechnologyConstants.GENERAL_TECHNOLOGY_ID ? selectedOriginalTechnology.id : undefined
                });
                break;
            case SearchQueryTypeEnum.ScienceArticle:
                PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ScienceSearch`, {
                    page: paginateToPage,
                    caseId: caseState.selectedCaseId,
                    technologyId: selectedOriginalTechnology.id !== TechnologyConstants.GENERAL_TECHNOLOGY_ID ? selectedOriginalTechnology.id : undefined
                });
                break;
            case SearchQueryTypeEnum.Patent:
                PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}PatentSearch`, {
                    page: paginateToPage,
                    caseId: caseState.selectedCaseId,
                    technologyId: selectedOriginalTechnology.id !== TechnologyConstants.GENERAL_TECHNOLOGY_ID ? selectedOriginalTechnology.id : undefined
                });
                break;
            default:
                break;
        }

        await webSocketController.invokeFunction("StartFindDocuments", filterVariables);
        setIsSearching(true);
        setIsQueryCollapsed(true);
    }, [caseState.selectedCaseId, savedQuery, selectedOriginalTechnology, selectedSearchQueryType, setModelledTopics, setSelectedModelledTopic, webSocketController]);

    const onClusteringCloseIconClick = () => {
        const firstModelledTopic = modelledTopics[0];
        if (firstModelledTopic?.name === "All") {
            setSelectedModelledTopic(firstModelledTopic);
        }
    };

    const onCancelSearch = () => {
        if (!searchId) return;
        webSocketController.invokeFunction("CancelSearch", searchId);
        PendoHelperSingleton.trackEvent(`${LoggingConstants.DYNAMIC_DELIVERY}CancelSearch`);
        setIsSearching(false);
    };

    const receiveAmountOfResult = (foundResultCount: number) => {
        setAmountOfResults(foundResultCount);
    };

    const receiveDocumentSearchResults = useCallback((documentSearchResults: TDocumentSearchStrategyResults): void => {
        setIsSearching(false);

        switch (selectedSearchQueryType) {
            case SearchQueryTypeEnum.ScienceArticle:
                if (documentSearchResults.scienceArticlesSearchResults) {
                    setScienceArticles(documentSearchResults.scienceArticlesSearchResults.map(
                        (scienceArticleSearchResult: TScienceSearchResult) => {
                            return buildMagDocumentDTOFromScienceSearchResult(scienceArticleSearchResult);
                        }
                    ));
                } else {
                    setScienceArticles([]);
                }
                setPatents([]);
                setShownPatents([]);
                break;
            case SearchQueryTypeEnum.Patent:
                if (documentSearchResults.scienceArticlesSearchResults) {
                    setScienceArticles(documentSearchResults.scienceArticlesSearchResults.map(
                        (scienceArticleSearchResult: TScienceSearchResult) => {
                            return buildMagDocumentDTOFromScienceSearchResult(scienceArticleSearchResult);
                        }
                    ));
                } else {
                    setScienceArticles([]);
                }

                if (documentSearchResults.patentsSearchResults) {
                    const patentDtos = documentSearchResults.patentsSearchResults.map(
                            (patent: TPatentSearchResult) => {
                                return fromPatentSearchResultToUsPatentDTO(patent);
                            }
                        );
                    setPatents(patentDtos);
                    setShownPatents(filterPatents(patentDtos));
                } else {
                    setPatents([]);
                    setShownPatents([]);
                }
                break;
            default:
                break;
        }
    }, [filterPatents, selectedSearchQueryType]);

    const receiveTopicModellingSearchResult = useCallback((newModelledTopics: TModelledTopicDTO[]): void => {
        if (newModelledTopics.length > 0) {
            setModelledTopics(newModelledTopics);
            setSelectedModelledTopic(newModelledTopics[0]);
        }
    }, [setModelledTopics, setSelectedModelledTopic]);

    const receiveEndProgress = useCallback(() => {
        setIsSearching(false);
        setSearchId(undefined);
    }, []);

    const receiveSnackbar = useCallback((toastType: string, message: string) => {
        if(toastType.toLowerCase() === "error") {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, message);
            PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ReceivedErrorSnackbar`, {
                error: message
            });
        }
    }, []);

    const receiveSearchId = useCallback((newSearchId: string) => {
        setSearchId(newSearchId);
    }, [setSearchId]);

    const receiveTermHitsCounts = useCallback((termHitCounts: {[key: string]: number}) => {
        if(!savedQuery) return;
        const newQuery = {...savedQuery};

        if(newQuery.environmentVariables.length > 0) {
            newQuery.environmentVariables = newQuery.environmentVariables.map((environment) => {
                const environmentKey = `env${environment.id}`;
                if(environmentKey in termHitCounts) {
                    environment.hitCountInCurrentQuery = termHitCounts[environmentKey];
                }
                return environment;
            });
        }

        if(newQuery.actionObjects.length > 0) {
            newQuery.actionObjects = newQuery.actionObjects.map((actionObject) => {
                const actionObjectKey = `sao${actionObject.id}`;
                if(actionObjectKey in termHitCounts) {
                    actionObject.hitCountInCurrentQuery = termHitCounts[actionObjectKey];
                }
                return actionObject;
            });
        }

        updateSavedQuery(newQuery);
    }, [savedQuery, updateSavedQuery]);

    const subscribeToResultFunctions = useCallback(async () => {
        await webSocketController.addHandler("DocumentSearchResults", receiveDocumentSearchResults);
        await webSocketController.addHandler("ReceiveTopicModellingSearchResult", receiveTopicModellingSearchResult);
        await webSocketController.addHandler("ReceiveDocumentCount", receiveAmountOfResult);
        await webSocketController.addHandler("ReceiveSearchID", receiveSearchId);
        await webSocketController.addHandler("ReceiveEndProgress", receiveEndProgress);
        await webSocketController.addHandler("ReceiveSnackbar", receiveSnackbar);
        await webSocketController.addHandler("ReceiveTermHitsCounts", receiveTermHitsCounts);
    }, [webSocketController, receiveDocumentSearchResults, receiveTopicModellingSearchResult, receiveSearchId, receiveEndProgress, receiveSnackbar, receiveTermHitsCounts]);

    const unsubscribeToResultFunctions = useCallback(() => {
        webSocketController.removeHandler("DocumentSearchResults", receiveDocumentSearchResults);
        webSocketController.removeHandler("ReceiveTopicModellingSearchResult", receiveTopicModellingSearchResult);
        webSocketController.removeHandler("ReceiveDocumentCount", receiveAmountOfResult);
        webSocketController.removeHandler("ReceiveSearchID", receiveSearchId);
        webSocketController.removeHandler("ReceiveEndProgress", receiveEndProgress);
        webSocketController.removeHandler("ReceiveSnackbar", receiveSnackbar);
        webSocketController.removeHandler("ReceiveTermHitsCounts", receiveTermHitsCounts);
    }, [webSocketController, receiveDocumentSearchResults, receiveTopicModellingSearchResult, receiveSearchId, receiveEndProgress, receiveSnackbar, receiveTermHitsCounts]);

    useEffect(() => {
        subscribeToResultFunctions();

        return () => unsubscribeToResultFunctions();
    }, [subscribeToResultFunctions, unsubscribeToResultFunctions]);

    const getQueryContainerClassName = (): string => {
        const classNameList = [styles.selectedQueryContainer];
        classNameList.push(styles.scrollbarCustomThin);
        if (isQueryCollapsed) classNameList.push(styles.collapsed);
        return classNameList.join(" ");
    };

    const getUserFlowQueryContainerClassName = (): string => {
        const classNameList = [styles.userFlowQueryContainer];
        if (!isQueryCollapsed) classNameList.push(styles.uncollapsed);
        return classNameList.join(" ");
    };

    const startMagHighlight = (documentId: number) => {
        if(!caseState.selectedCaseId) return;
        const queryId = savedQuery ? savedQuery.guid : null;
        webSocketController.invokeFunction("OviHighlightArticle", caseState.selectedCaseId, documentId, 1, null, queryId);
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}HighlightScienceArticle`, {
            caseId: caseState.selectedCaseId, 
            documentId: documentId,
            queryId: queryId
        });
    };

    const startPatentHighlight = (documentId: string) => {
        if(!caseState.selectedCaseId) return;
        const queryId = savedQuery ? savedQuery.guid : null;
        // This regex checks if the document id is only numbers
        const isMagPatent = RegexConstants.IS_DIGITS_ONLY.test(documentId.trim());
        webSocketController.invokeFunction("OviHighlightArticle", caseState.selectedCaseId, documentId,
            isMagPatent ? 1 : 2, null, queryId);
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}HighlightPatent`, {
            caseId: caseState.selectedCaseId, 
            documentId: documentId,
            queryId: queryId
        });
    };

    const addToCaseQueryCount = (count: number) => {
        setCaseQueryCount(caseQueryCount + count);
        caseDispatch({ type: CaseActionTypeEnum.SetCaseQueryCount, newQueryCount: caseQueryCount + count } as ICaseSetQueryCount);
    };

    const addToTechnologyQueryCount = (technologyId: number, count: number) => {
        const newList = [];

        for (const technology of technologies) {
            if(technology.id === technologyId) {
                if(technology.queryCount !== undefined) technology.queryCount += count;
            }

            newList.push(technology);
        }

        setTechnologies(newList);
    };

    useEffect(() => {
        if (!selectedOriginalTechnology) return;

        caseDispatch({
            type: CaseActionTypeEnum.SetCaseSelectedTechnology,
            selectedOriginalTechnologyId: selectedOriginalTechnology.id,
            selectedTechnologyId: undefined,
            selectedEntityName: "",
            selectedEntityNumbering: ""
        } as ICaseSelectedTechnology);

    }, [caseDispatch, selectedOriginalTechnology]);

    const startClusteringButtonOnClickHandler = useCallback((): void => {
        setAmountOfResults(undefined);
        handleSearchOnClick(1, true);
    }, [handleSearchOnClick]);

    useEffect(() => {
        if (isTopicModellingSearchStarted) {
            startClusteringButtonOnClickHandler();
            setIsTopicModellingSearchStarted(false);
        }
    }, [isTopicModellingSearchStarted, setIsTopicModellingSearchStarted, startClusteringButtonOnClickHandler]);

    const selectedModelledTopicIndex = modelledTopics.findIndex(topic => topic.name === selectedModelledTopic?.name);

    const renderSearchButtonComponent = () => {
        if (isSavedQueriesLoading || isConceptListLoading) {
            return <div></div>;
            // return <SearchButtonLoader />;
        }
        if (savedQuery && !isConceptListLoading && !isSavedQueriesLoading)  {
            return (
                <FindestButton title="Search" onClick={() => { setAmountOfResults(undefined); handleSearchOnClick(1); }} />
            );
        }
        return null;
    };

    return (
        <>
            <AsynchronousWrapper didError={didConceptListError} errorMessage={conceptListErrorMessage} isLoading={false} LoaderComponent={null}>
                <div className={getUserFlowQueryContainerClassName()}>
                    <div className={styles.querySelectionContainer}>
                        {caseState.selectedCaseId ?
                            <SavedQueries
                                technologies={technologies}
                                selectedTechnology={selectedOriginalTechnology}
                                handleSelectedTechnologyChange={setSelectedOriginalTechnology}
                                caseQueryCount={caseQueryCount}
                                isLoading={isConceptListLoading}
                            />
                            :
                            null
                        }
                        {renderSearchButtonComponent()}
                    </div>
                    { caseState.selectedCaseId && selectedOriginalTechnology ?
                        <QueryTabs
                            caseId={caseState.selectedCaseId}
                            selectedTechnologyId={selectedOriginalTechnology.id} 
                            addToCaseQueryCount={addToCaseQueryCount}
                            addToTechnologyQueryCount={addToTechnologyQueryCount}
                            isLoading={isConceptListLoading}
                        />
                        : null
                    }
                    {savedQuery ?
                        <div className={getQueryContainerClassName()}>
                            <SavedQueryFields isSpecificationUnitsEnabled={true} />
                            <div className={styles.selectedQuerySearchFieldsContainer}>
                                <FindDocumentsQueryFields
                                    selectedSearchQueryType={selectedSearchQueryType}
                                    handleSearchQueryTypeChange={setSelectedSearchQueryType}
                                />
                            </div>
                        </div>
                        :
                        <></>
                    }
                    {/*(isConceptListLoading || isSavedQueriesLoading) && <ViewQueryLoader />*/}
                    {savedQuery && !isConceptListLoading && !isSavedQueriesLoading ?
                        <div className={styles.toggleVisibleQuery}>
                            {isQueryCollapsed ?
                                <div onClick={() => { setIsQueryCollapsed(false); }}>View/Edit Query<FontAwesomeIcon icon={faChevronDown} /></div>
                                :
                                <div onClick={() => { setIsQueryCollapsed(true); }}>Hide Query<FontAwesomeIcon icon={faChevronUp} /></div>
                            }
                        </div>
                        :
                        <></>
                    }
                </div>
            </AsynchronousWrapper>
            <div className={styles.userFlowContentContainer}>
                <div className={styles.headerContainer}>
                    <div className={styles.headerLeftSide}>
                        {amountOfResults ? 
                            <div className={styles.numberOfResults}>{amountOfResults} {amountOfResults === 1 ? "result" : "results"}</div> 
                            : null}
                        {!isSearching && isCurrentUserFindest && amountOfResults && amountOfResults > 0 && !isTopicModellingSearch ? 
                            <FindestButton iconName={faCircleNodes} title="Cluster results" onClick={startClusteringButtonOnClickHandler} extraClassName={styles.clusterResultsButton}/>  
                            : 
                                null
                        }
                        {!isSearching && amountOfResults && amountOfResults > 0 && isTopicModellingSearch && selectedModelledTopicIndex !== 0 ?
                            <BorderedIconedTextblock
                                text={`Cluster #${selectedModelledTopicIndex}`}
                                title="Cluster name"
                                className={styles.clusterTextBlock}
                                backgroundColor={variables.primaryBackgroundColor}
                                color="white"
                                icon={faTimesCircle}
                                onIconClick={onClusteringCloseIconClick}
                            />
                            : null
                        }
                    </div>
                    {amountOfResults ? 
                        <Pagination currentPagePosition={currentPage} maximumPagePosition={amountOfPages} goToPreviousPage={paginateToPreviousPage} goToNextPage={paginateToNextPage} /> :
                        null} 
                </div>
                {isSearching ? <div className={styles.loadingIndicatorContainer}><RotatingCircleLoadingIndicator onCancel={onCancelSearch} /></div> : null}
                <div className={[styles.contentContainer, styles.scrollbarCustomThin].join(" ")}>
                    {scienceArticles.length > 0 ?
                        scienceArticles.map((scienceArticle, index) => {
                            return (
                                <div key={`${scienceArticle.id}-${index}`}>
                                    <MagDocument documentDto={scienceArticle} onClick={startMagHighlight} />
                                </div>
                            );
                        })
                        : null}

                    {shownPatents.length > 0 ?
                        shownPatents.map((patent, index) => {
                            return (
                                <div key={`${patent.id}-${index}`}>
                                    <UsPatentDocument key={`${patent.id}-${index}`} patentDto={patent} onClick={startPatentHighlight} />
                                </div>
                            );
                        })
                        : null}
                </div>
            </div>
        </>
    );
};
